import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import type { UtbmsCode, UtbmsCodeSet } from '../types/billing.types';
import { UTBMS_CODE_SET_LABELS } from '../types/billing.types';

interface UtbmsCodeSelectorProps {
  value: string;
  onChange: (code: string) => void;
  /** Filter to specific code set (e.g. 'expense' for expense forms) */
  codeSet?: UtbmsCodeSet;
  /** Show required indicator */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Label text */
  label?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Additional className for the wrapper */
  className?: string;
}

const API_BASE = '';

const UtbmsCodeSelector: React.FC<UtbmsCodeSelectorProps> = ({
  value,
  onChange,
  codeSet,
  required = false,
  placeholder = 'Search UTBMS code...',
  label = 'UTBMS Code',
  disabled = false,
  className = '',
}) => {
  const { keycloak } = useKeycloak();
  const [codes, setCodes] = useState<UtbmsCode[]>([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Fetch enabled codes from API
  useEffect(() => {
    if (!keycloak?.token) return;

    const fetchCodes = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (codeSet) params.set('code_set', codeSet);
        const url = `${API_BASE}/api/v1/billing/utbms-codes/enabled${params.toString() ? `?${params}` : ''}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${keycloak.token}` },
        });
        if (res.ok) {
          const json = await res.json();
          setCodes(json.data || []);
        }
      } catch {
        // Silently fail — will show empty dropdown with free-text fallback
      } finally {
        setLoading(false);
      }
    };

    fetchCodes();
  }, [keycloak?.token, codeSet]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Filter codes by search term
  const filtered = codes.filter((c) => {
    const term = search.toLowerCase();
    return (
      c.code.toLowerCase().includes(term) ||
      c.description.toLowerCase().includes(term) ||
      (c.phase?.toLowerCase().includes(term) ?? false)
    );
  });

  // Group by code set for display
  const grouped = filtered.reduce<Record<string, UtbmsCode[]>>((acc, c) => {
    const key = c.code_set;
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  const handleSelect = useCallback((code: string) => {
    onChange(code);
    setSearch('');
    setOpen(false);
  }, [onChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    onChange(val); // Allow free-text entry
    if (!open) setOpen(true);
  };

  // Display value: show code description when a valid code is selected
  const selectedCode = codes.find((c) => c.code === value);
  const displayValue = open ? search : (selectedCode ? `${value} — ${selectedCode.description}` : value);

  // If no codes loaded (UTBMS disabled), render plain text input
  if (!loading && codes.length === 0) {
    return (
      <div className={className}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label}{required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
        )}
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        />
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <input
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onFocus={() => { setOpen(true); setSearch(value || ''); }}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
      />
      {value && !disabled && (
        <button
          type="button"
          onClick={() => { onChange(''); setSearch(''); }}
          className="absolute right-2 top-[calc(50%+10px)] -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
        >
          &times;
        </button>
      )}

      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg">
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Loading codes...</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              {search ? 'No matching codes' : 'No codes available'}
            </div>
          ) : (
            Object.entries(grouped).map(([setKey, setCodes]) => (
              <div key={setKey}>
                {!codeSet && (
                  <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                    {UTBMS_CODE_SET_LABELS[setKey as UtbmsCodeSet] || setKey}
                  </div>
                )}
                {setCodes.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => handleSelect(c.code)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-lex-state-hover transition-colors ${
                      c.code === value ? 'bg-lex-accent-soft font-medium' : ''
                    }`}
                  >
                    <span className="font-mono text-lex-accent mr-2">{c.code}</span>
                    <span className="text-gray-700 dark:text-gray-300">{c.description}</span>
                    {c.phase && (
                      <span className="ml-2 text-xs text-gray-400">({c.phase})</span>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default UtbmsCodeSelector;
